import {
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  Optional,
  AfterViewInit,
  ChangeDetectorRef
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import type { ITreeOptions, TreeComponent, TreeModel, TreeNode } from '@odymaui/angular-tree-component';
import { Geography } from '../../models/geography.model';
import { UserGeography } from '../../models/user-geography.model';
import { TreeNodeService } from '../../../core/services/tree-node.service';
import { GeographyMenuOption } from '../../models/geography-menu-option.model';
import { IGeographyNode } from '../../interfaces/geography-node.interface';
import { GeographyService } from '../../../core/services/geography.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeModule } from '@odymaui/angular-tree-component';
import { SearchBarComponent } from '../../../features/search-bar/search-bar';
import { Product } from '../../models/product.model';
import { ContextMenuComponent } from '../context-menu/context-menu';
import { ProductMenuOption } from '../../models/product-menu-option.model';
import { ProductTreeService } from '../../../core/services/product-tree.service';
import { SELECTOR_COMMON_CONSTANTS } from '../../constants/selector.constant';

export type TreeType = 'geography' | 'product';

@Component({
  selector: 'emma-tree-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeModule,
    SearchBarComponent,
    ContextMenuComponent,
  ],
  templateUrl: './tree-view.component.html',
  styleUrl: './tree-view.component.scss',
})
export class TreeViewComponent implements OnChanges {
  @ViewChild('tree')
  public tree!: TreeComponent;
  @ViewChild(SearchBarComponent) public searchBar!: SearchBarComponent;
  @Input() public dialogTreeData: Array<any> = [];
  @Input() public isDuplicateRequest : boolean | undefined= false;
  @Input() public treeOptions: ITreeOptions = {};
  @Output() public newItemEvent = new EventEmitter<any>();
  @Output() public selectedItemEvent = new EventEmitter<Array<any>>();
  @Output() public closeEvent = new EventEmitter<any>();
  @Input() public parentComponent = '';
  @Input() public selectedNode: Geography | Array<Product> | undefined  | null;
  @Input() public userGeographies: Array<UserGeography> = [];
  @Input() public treeType: TreeType = 'geography';
  public selectedItem: Array<any> = [];
  public dropdownCloseFlag = false;
  public menuItemClicked = false;
  public nonAccessibleGeographyCodes: Array<string> = [];
  public highlightedNodeId: number | null = null; 
  public isRootNodeSelected = false;
  public readonly ROOT_NODE_ID : number = 9953;
  public readonly ROOT_NODE_NAME : string = "Alcoholic Drinks";
  
  public constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any | null,
    private readonly treeMappingService: TreeNodeService,
    private readonly geographyService: GeographyService,
    private readonly cdr: ChangeDetectorRef,
    @Optional() public dialogRef: MatDialogRef<TreeViewComponent>,
    private readonly productTreeService: ProductTreeService
  ) {
    if (!this.data) {
      this.data = {};
    }
  }

  public get isProductTree(): boolean {
    return this.treeType === 'product';
  }

  public get isGeographyTree(): boolean {
    return this.treeType === 'geography';
  }

  public focusSearchBar(): void {
    if (this.searchBar) {
      setTimeout(() => {
        this.searchBar.focusSearchInput();
      }, 100);
    }
  }

  public preventDropdownClose(event: Event): void {
    event.stopPropagation();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dialogTreeData']) {
      if (this.tree && this.tree.treeModel) {
        setTimeout(() => {
          this.tree.treeModel.update();
          if (this.tree.treeModel.roots && this.tree.treeModel.roots.length > 0) {
            this.tree.treeModel.roots.forEach((root: any) => {
              root.expandAll();
            });
          }
        }, 50);
      }
    }
    if (changes['selectedNode']) {
      this.setDefaultSelected();
    }
  }

  public onInitialized(): void {
    setTimeout(() => {
      try {
        if (!this.tree || !this.tree.treeModel) {
          return;
        }

        if (this.isProductTree) {
          if (this.tree.treeModel.getFirstRoot()) {
            this.tree.treeModel.getFirstRoot().expand();
            this.refreshProductNodesMenuOptions(true);
          } else {
            this.tree.treeModel.expandAll();
            this.refreshProductNodesMenuOptions(true);
          }
        }

        const treeModel: TreeModel = this.tree.treeModel;
        const nodes = treeModel.nodes;

        if (this.data?.defaultSelected) {
          this.selectNodeById(nodes, this.data.defaultSelected, 'id', true);
        }

        this.setDefaultSelected();

        if (this.userGeographies && this.userGeographies.length > 0) {
          this.filterGeographyNodesByPermission(nodes);
        }
      } catch (error) {
        // Silently handle initialization errors
      }
    }, 100);
  }

  public filterGeographyNodesByPermission(nodes: Array<any>) {
    if (nodes && nodes[0]?.emiCode) {
      nodes.some((nodeItem) => {
        if (
          this.userGeographies.length > 0 &&
          !this.userGeographies.some(
            (geography) => geography.geographyCode === nodeItem.emiCode
          )
        ) {
          nodeItem.isSelected = nodeItem.isSelectable = false;
          nodeItem.isDisabled = true;
          this.nonAccessibleGeographyCodes.push(nodeItem.emiCode);
        }
        if (nodeItem.children) {
          this.filterGeographyNodesByPermission(nodeItem.children);
        }
      });
    }
  }

  public selectNodeById(
    nodes: Array<any>,
    idToSelect: string | number,
    property: string,
    isSelected: boolean
  ): void {
    nodes.some((nodeItem) => {
      if (nodeItem[property] === idToSelect) {
        nodeItem.isSelected = isSelected;
        nodeItem.isDisabled = isSelected;
        this.tree.treeModel
          .getNodeById(nodeItem?.id)
          ?.setIsSelected(isSelected);
        this.onSelectedNodeWithCheckBox(
          this.tree.treeModel.getNodeById(nodeItem?.id)
        );
        return true;
      }
      if (nodeItem.children) {
        this.selectNodeById(
          nodeItem.children,
          idToSelect,
          property,
          isSelected
        );
      }
      return false;
    });
  }

  public closeDropdowm(): void {
    if (this.dropdownCloseFlag) {
      if(Array.isArray(this.selectedNode) && this.selectedNode.length > 0) {
        if(this.selectedNode[0].sector) {
          this.dropdownCloseFlag = false;
          return;
        }
      }
      this.closeEvent.emit();
      this.dropdownCloseFlag = false;
    }
  }
  public onGeographyMenuClick(
    menuItem: GeographyMenuOption,
    selectedNode: IGeographyNode
  ): void {
    this.menuItemClicked = true;
    this.selectedItem = this.treeMappingService.handleGeographyMenuClick(
      menuItem,
      selectedNode,
      this.userGeographies,
      this.dialogTreeData,
      this.geographyService
    );
    this.refreshNodesMenuOptions();
  }

  
 
  public onClick(event: any) {
    if (this.menuItemClicked) {
      event.node.data.isDisabled = false;
      event.node.data.isSelected = !event.node.data.isSelected;
      event.node.expand();
    }
  }
  public selectonChange(event: { node: { data: any } }, isSelected: boolean): void {
    if (isSelected) {
      this.selectedItem.push(event.node.data);
      event.node.data.isSelected = true;
    } else {
      event.node.data.isSelected = false;
      this.selectedItem = this.selectedItem.filter(
        (x) => x.name !== event.node.data.name
      );
    }
    this.tree.treeModel.update();
    this.selectedItemEvent.emit(JSON.parse(JSON.stringify(this.selectedItem)));
  }

  public onSelectedNodeChange(event: any): void {
    const selectedNode = event;
    if (selectedNode) {
      this.highlightedNodeId = selectedNode.id;
      
      this.treeMappingService.expandToNode(this.dialogTreeData, selectedNode.id);
      
      if (this.tree?.treeModel) {
        // Add to selectedItem array if not already present
        const isAlreadySelected = this.selectedItem.some(
          (item) => item.id === selectedNode.id
        );
        
        if (!isAlreadySelected) {
          this.selectedItem.push(selectedNode);
        }
        
        // Update isSelected property for ALL nodes in dialogTreeData
        const updateAllSelections = (nodes: Array<any>): void => {
          for (const node of nodes) {
            // Check if this node is in selectedItem array
            const shouldBeSelected = this.selectedItem.some(item => item.id === node.id);
            node.isSelected = shouldBeSelected;
            
            if (node.children && node.children.length > 0) {
              updateAllSelections(node.children);
            }
          }
        };

        updateAllSelections(this.dialogTreeData);
        
        this.tree.treeModel.update();
        
        this.selectedItem.forEach(item => {
          const node = this.tree.treeModel.getNodeById(item.id);
          if (node && node.data) {
            node.data.isSelected = true;
          }
        });
        
        this.updateNodeHighlights();
        this.selectedItemEvent.emit(JSON.parse(JSON.stringify(this.selectedItem)));
        
        this.cdr.detectChanges();
      }
    }
  }

  private updateDialogTreeNode(nodeId: number, isSelected: boolean): void {
    const updateNode = (nodes: Array<any>): boolean => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          node.isSelected = isSelected;
          return true;
        }
        if (node.children && node.children.length > 0) {
          if (updateNode(node.children)) {
            return true;
          }
        }
      }
      return false;
    };
    updateNode(this.dialogTreeData);
  }

  @HostListener('document:queries.setdropdownCloseFlag', ['$event'])
  public onDatasetSelection(): void {
    this.dropdownCloseFlag = true;
  }

  @HostListener('document:expandAllTreeNodesEvent', ['$event'])
  public expandTree($event: any): void {
  const nodeIds = $event.detail;
  const treeModel: TreeModel = this.tree.treeModel;
  if (treeModel.nodes && treeModel.nodes?.length > 0) {
    this.expandTreeNodes(treeModel.nodes, nodeIds);
  }
}

  public setDefaultSelected = (): void => {
    if (this.selectedNode && this.tree) {
      this.findNodeByName(this.selectedNode)?.setIsActive(true);
    }
  };
  public onSelectedNodeWithCheckBox(node: any): void {
    if (!this.menuItemClicked) {
      const nodeData = node.data;
      const isProductSelected = this.selectedItem.some(
        (item) => item.id === nodeData?.id
      );

      if (nodeData?.isSelected && !isProductSelected) {
        this.selectedItem.push(nodeData);
        this.highlightedNodeId = nodeData.id;
      }
      if (!nodeData?.isSelected) {
        this.selectedItem = this.selectedItem.filter(
          (item) => item.id !== nodeData?.id
        );

        if (this.highlightedNodeId === nodeData.id) {
          this.highlightedNodeId = null;
        }
      }

    } else {
      const treeModel: TreeModel = this.tree.treeModel;
      this.expandParentTreeNodes(treeModel.nodes);
      this.menuItemClicked = false;
    }

    this.updateNodeHighlights();
    
    this.selectedItemEvent.emit(JSON.parse(JSON.stringify(this.selectedItem)));
  }

  public onManualNodeSelect(node: any): void {
    const result = this.treeMappingService.handleManualNodeSelect(
      node,
      this.selectedItem,
      this.highlightedNodeId
    );
    
    this.selectedItem = result.selectedItems;
    this.highlightedNodeId = result.highlightedNodeId;
    
    if (this.tree?.treeModel) {
      this.tree.treeModel.update();
    }
    
    this.selectedItemEvent.emit(JSON.parse(JSON.stringify(this.selectedItem)));
  }

  public toggleNode(node: any): void {
    this.treeMappingService.toggleNode(node);
  }

  public expandParentTreeNodes(nodes: Array<any>): void {
    if (nodes && nodes?.length > 0) {
      nodes.forEach((item) => {
        const nodeToSelect = this.tree.treeModel.getNodeById(item?.id);
        if (nodeToSelect && item.isSelected) {
          this.tree.treeModel
            .getNodeBy((node: { data: { emiCode: any; }; }) => node?.data.emiCode === item.parent?.emiCode)
            ?.expand();
        }
        if (item.isDisabled) {
          item.isSelected = true;
        }
        if (this.nonAccessibleGeographyCodes.includes(item?.emiCode)) {
          item.isSelected = false;
          item.isDisabled = true;
          this.selectedItem = this.selectedItem.filter(
            (x) => x.emiCode !== item?.emiCode
          );
        }
        if (item.children?.length > 0) {
          this.expandParentTreeNodes(item.children);
        }
      });
    }
  }
  public findNodeByName = (
    nodeName:  Geography | Array<Product>
  ): TreeNode | null => {
    if (Array.isArray(nodeName)) {
      for (const test of nodeName) {
        const foundNode = this.tree?.treeModel?.getNodeBy(
          (node: TreeNode) => node?.data?.id === test.id
        );
        if (foundNode) {
          return foundNode;
        }
      }
    } else {
      return this.tree?.treeModel?.getNodeBy(
        (node: TreeNode) => node?.data?.name === nodeName.name
      );
    }
    return null;
  };

  public expandTreeNodes(nodes: Array<any>, nodeIds: any): void {
    if (nodeIds?.length === 0 && !nodes[0].sector) {
      nodes.forEach((item) => {
        const nodeToBeExpanded = this.tree.treeModel.getNodeById(item?.id);
        if (nodeToBeExpanded && nodeToBeExpanded.children?.length > 0) {
          nodeToBeExpanded.expand();
        }
        if (item.children?.length > 0) {
          this.expandTreeNodes(item.children, nodeIds);
        }
      });
    } else if (nodeIds?.length > 0) {
      nodeIds.forEach((item: any) => {
        const nodeToBeExpanded = this.tree.treeModel.getNodeById(item);
        if (nodeToBeExpanded && nodeToBeExpanded.children?.length > 0) {
          nodeToBeExpanded.expand();
        }
      });
    }
  }

  private refreshNodesMenuOptions(): void {
    this.treeMappingService.refreshGeographyNodesMenuOptions(
      this.dialogTreeData,
      this.tree as any,
      this.userGeographies,
      this.geographyService
    );
  }

  private updateNodeHighlights(): void {
    this.cdr.detectChanges();
    setTimeout(() => {
      if (!this.tree?.treeModel) return;
      
      const treeElement = document.querySelector('#e-tree');
      if (!treeElement) return;
      const allNodeWrappers = treeElement.querySelectorAll('.node-wrapper');
      allNodeWrappers.forEach(wrapper => {
        wrapper.classList.remove('highlighted');
      });
      
      if (this.highlightedNodeId !== null) {
        const isSelected = this.selectedItem.some(item => item.id === this.highlightedNodeId);
        
        if (isSelected) {
          const highlightedLabel = treeElement.querySelector(`[data-node-id="${this.highlightedNodeId}"]`);
          if (highlightedLabel) {
            const nodeWrapper = highlightedLabel.closest('.node-wrapper');
            if (nodeWrapper) {
              nodeWrapper.classList.add('highlighted');
            }
          }
        } else {
          this.highlightedNodeId = null;
        }
      }
    }, 10);
  }

   public onProductMenuClick(menuItem: ProductMenuOption, selectedNodeId: number): void {
    if (menuItem.isSelected) {
      this.onDeselectMenuClick(selectedNodeId, menuItem.level);
      return;
    }
    this.onSelectMenuClick(selectedNodeId, menuItem.level);
  }

    private updateRootSelection(): void {
    const rootProductNode = this.dialogTreeData.find(n => n.id === this.ROOT_NODE_ID || n.name === this.ROOT_NODE_NAME);
    if (rootProductNode) {
      rootProductNode.isSelected = this.isRootNodeSelected;
    }
  }

  private updateMenuSelection(
    nodeId: number,
    optionLevel: number,
    isSelect: boolean
  ): void {
    const SCROLL_CONTAINER_SELECTOR = '#e-tree';
    const treeElement = document.querySelector(SCROLL_CONTAINER_SELECTOR);
    let previousScrollTop = 0;

    if (treeElement) {
      previousScrollTop = treeElement.scrollTop;
    }

    this.dialogTreeData = isSelect
      ? this.productTreeService.selectProductMenuOption(this.dialogTreeData, nodeId, optionLevel)
      : this.productTreeService.deselectProductMenuOption(this.dialogTreeData, nodeId, optionLevel);

    const affectedNode = this.tree.treeModel.getNodeById(nodeId);
    if (affectedNode) {
      affectedNode.data = { ...affectedNode.data };
    }
    this.tree.treeModel.update();
    this.cdr.detectChanges();
    if (treeElement) {
      setTimeout(() => {
        treeElement.scrollTop = previousScrollTop;
      }, 0);
    }
    const rootProductNode = this.dialogTreeData.find(
      node => node.id === this.ROOT_NODE_ID
    );
    this.isRootNodeSelected = rootProductNode?.isSelected ?? false;
    this.onSelectedProductNodeChange();
  }

  public onSelectedProductNodeChange(fromCheckbox: boolean = false): void {
  setTimeout(() => {
    if (!fromCheckbox) {
      this.updateRootSelection();
    }

    this.selectedItem =
      this.productTreeService.flattenProductSelections(this.dialogTreeData);

    this.selectedItemEvent.emit([...this.selectedItem]);
    this.refreshProductNodesMenuOptions();
  }, SELECTOR_COMMON_CONSTANTS.numberFifty);
}

  public onSelectMenuClick(nodeId: number, optionLevel: number): void {
    this.updateMenuSelection(nodeId, optionLevel, true);
  }

  public onDeselectMenuClick(nodeId: number, optionLevel: number): void {
    this.updateMenuSelection(nodeId, optionLevel, false);
  }

   public refreshProductNodesMenuOptions(isSearched: boolean = false): void {
    this.dialogTreeData.forEach(node => this.productTreeService.iterateNodesToRefresh(node, this.tree, isSearched, true));
  }

  public clearSelection(): void {
    const clearNodeSelection = (nodes: Array<any>): void => {
      for (const node of nodes) {
        node.isSelected = false;
        if (node.children && node.children.length > 0) {
          clearNodeSelection(node.children);
        }
      }
    };
    clearNodeSelection(this.dialogTreeData);
    this.selectedItem = [];
    if (this.tree && this.tree.treeModel) {
      this.tree.treeModel.update();
    }
    this.selectedItemEvent.emit([]);
    this.highlightedNodeId = null;
    this.cdr.detectChanges();
  }
}
